import {
  useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ClicReact,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSesion } from '../lib/sesion';
import { ApiError, dhaliaApi } from '../lib/api';
import { permisosDeSesion, ubicacionesDe } from '../lib/navegacion';
import { AVATAR_DHALIA, HERO_DHALIA } from '../imagenes/dhalia';

/**
 * DHALIA — Asistente Virtual GrupoDH.
 *
 * Porte del bloque `PMBOT v71` del monolito (@90.527.707 → @90.598.609).
 *
 * ── POR QUÉ NO SE VEÍA ──────────────────────────────────────────────────────
 * Por dos razones distintas, y las dos hay que saberlas:
 *  1. En el **v88** el motor está entero y **apagado a mano**: el archivo
 *     termina con un `<style id="pmbot-hide">` (@94.226.620) cuyo propio
 *     comentario dice «DHALIA oculta para TODOS los usuarios (motor intacto).
 *     Para reactivar la asistente: eliminar este bloque». O sea que quien mire
 *     el monolito tampoco la ve.
 *  2. En el **porte** no existía: no había ningún componente.
 *
 * ── QUÉ HACE, Y QUÉ NO ──────────────────────────────────────────────────────
 * El v88 tiene dos modos y el porte respeta los dos:
 *  · **local** (el que está activo): responde por dónde se llega a cada sección
 *    —resolviendo la ruta con los permisos DEL USUARIO, no con una lista fija—
 *    y preguntas de uso del portal. **No da cifras**, y no es una limitación
 *    del porte: es la política del propio monolito, que ante cualquier pregunta
 *    de datos contesta «Yo no comparto cifras 😉» y lleva a la sección donde
 *    consultarlas con los filtros del usuario (`iRuta`, y por eso sus intents
 *    `iTotal`/`iTop`/`iSerie` son código muerto en la práctica).
 *  · **IA en línea**: desde el 2026-08-14 (D-1 respondida) va contra el relé
 *    `/api/dhalia/chat` — una Netlify Function que llama a Claude con la clave
 *    en variables de entorno del servidor. El mecanismo del v88 (`config url`,
 *    `localStorage`) se retiró tal como fijó `ESTADO_DHALIA.md` §8.6. Si el
 *    despliegue no tiene `ANTHROPIC_API_KEY`, el sondeo devuelve «no
 *    disponible» y la asistente sigue 100 % local, sin costo alguno.
 *
 * ── LO QUE SE ADAPTÓ A PROPÓSITO, Y NO ES UN RECORTE ────────────────────────
 * Las respuestas de uso del v88 describen el MONOLITO: un cajón de FILTROS
 * global, `Ctrl+K`, un interruptor de tema y «Descargar Detalle» en todas las
 * secciones. Este portal no tiene eso todavía —los filtros son de cada página,
 * y la exportación existe hoy en Cartera, Bitácora FTD y Power Query (el
 * tablero de C.E.I. trae la suya)—. Copiar
 * esos textos verbatim sería mandar a la usuaria a botones que no existen, así
 * que las respuestas dicen lo que ESTE portal hace y avisan cuando algo aún no
 * está migrado. El banco de RUTAS sí va verbatim: son secciones, no botones.
 *
 * La otra diferencia medida: el v88 reconoce nombres de cliente, vendedor y SKU
 * porque tiene el cubo `_RAW` entero en memoria. Aquí los datos viven en la
 * base, así que se reconocen las **marcas y empresas** —que son hechos fijos
 * del dominio— y de lo demás se detecta la DIMENSIÓN por la pregunta. Eso solo
 * cambia el «Para <b>X</b> (Cliente):» de cortesía; la ruta que se responde es
 * la misma, porque el v88 la elige por dimensión y no por entidad.
 */

/* ─────────────────────────── utilidades del v88 ─────────────────────────── */

/** `nrm` del v88: minúsculas, sin acentos, solo letras/dígitos y espacios. */
function nrm(t: string): string {
  return String(t || '').toLowerCase().normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9ñ\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** `esc` del v88. Todo lo que venga de la usuaria pasa por aquí antes del HTML. */
function esc(t: unknown): string {
  return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/* ───────────────────────── banco de rutas (v87) ─────────────────────────── */

interface DefRutaBot { k: string[]; pg: string; t: string; how: string }

/** `RUTAS` del bloque del bot, verbatim: palabras clave → página y cómo se usa. */
const RUTAS_BOT: DefRutaBot[] = [
  { k: ['go to market', 'gtm', 'portafolio por tipo', 'tipo de cliente'], pg: 'gtm', t: 'Go To Market',
    how: 'Navegación Coordinador → Zona → Atención → Segmento → Cliente, con el portafolio por tipo de cliente (GA.MA = Eléctricos).' },
  { k: ['lanzamiento', 'lanzamientos', 'nuevos productos', 'canibalizacion', 'frecuencia de compra', 'kit de rizos', 'kits', 'argan'], pg: 'lanzamientos', t: 'Lanzamientos',
    how: 'Las 6 tarjetas del landing (5 kits + HD Cosmetic\'s). Dentro de cada kit: unidades netas, cubo región × serie × mes, clientes de la línea, forecast a 3 meses y —en Argán— el bloque de recompras. Ojo: los tres bloques tienen FECHAS DE CORTE distintas y la página lo declara.' },
  { k: ['comportamiento de un cliente', 'comportamiento del cliente', 'cliente especifico', 'ventas de un cliente', 'ventas por cliente', 'compras de un cliente', 'buscar un cliente', 'codigo de cliente', 'clientes'], pg: 'clientes', t: 'Clientes',
    how: 'En <b>Comportamiento general</b> se busca el cliente por nombre o código. En el mismo submódulo: Tendencia, Análisis, Activación / Inactivación y Maestra.' },
  { k: ['clientes activos', 'clientes inactivos', 'clientes perdidos', 'clientes nuevos', 'activacion'], pg: 'cli-activacion', t: 'Activación / Inactivación',
    how: 'Clientes que entran y salen de compra por período. Ojo con la definición: «activo» es tener al menos una FILA con venta > 0, no un total > 0.' },
  { k: ['sku especifico', 'panel 360', '360 del sku', 'detalle de un sku', 'comportamiento de un sku', 'articulo especifico', 'producto especifico', 'sku', 'skus'], pg: 'marcas', t: 'Marcas · detalle de SKU',
    how: 'Se entra por la marca y desde ahí al SKU. El panel 360° del monolito <b>todavía no está migrado</b>: mientras tanto, Análisis Financiero → pestaña «Categorías y SKUs» da el SKU en USD.' },
  { k: ['ventas de una marca', 'ventas por marca', 'marca', 'marcas', 'bbk', 'pasarela', 'quick fix', 'hd cosmetics', 'quickfix'], pg: 'marcas', t: 'Marcas',
    how: 'Landing con las 6 tarjetas y detalle por marca con geografía y rango. Para las mismas cifras en USD: Análisis Financiero → pestaña «Por marca».' },
  { k: ['vendedor', 'vendedores', 'fuerza de ventas', 'ventas de un vendedor'], pg: 'vendedor', t: 'Vendedores',
    how: 'Panel general por vendedor; en el mismo submódulo: Evolución, Análisis y Comportamiento. Tu usuario determina qué vendedores ves — y eso se aplica en la base, no escondiendo filas.' },
  { k: ['meta', 'metas', 'cumplimiento', 'objetivos comerciales'], pg: 'metas', t: 'Metas',
    how: 'Cumplimiento por grupo de empresa. Ojo: la meta va en <b>cajas</b> para DDH/DHV y en <b>unidades</b> para Difranca, y los dos grupos no se suman nunca.' },
  /* `mer037` se conserva SOLO como palabra de búsqueda, nunca como etiqueta: no
   * es un código de este módulo —es el del cliente KOLORES, ver la nota de
   * `negociaciones.mts`— pero mucha gente lo aprendió así y tiene que seguir
   * encontrando la página al escribirlo. */
  { k: ['negociacion', 'negociaciones', 'mer037', 'aporte', 'registrar una negociacion'], pg: 'nego', t: 'Negociaciones Comerciales',
    how: 'Solicitud de actividades, descuentos y promociones en PDV. El techo <b>ya no es el 3 %</b>: cada asesor tiene un presupuesto por trimestre y la solicitud se descuenta de su bolsa. Si cruza su umbral de consumo, queda <b>pendiente de aprobación</b> del coordinador.' },
  { k: ['cartera', 'cartera de clientes', 'base de clientes'], pg: 'cart-clientes', t: 'Cartera de Clientes',
    how: 'Seis reportes en el submenú. <b>Los importes de cartera son BRUTOS, con el IVA del 16 %</b>: no son comparables con los del Análisis Financiero sin dividir por 1,16, y la página lo avisa.' },
  { k: ['cuentas por cobrar', 'cxc', 'por cobrar', 'vencidas', 'facturas vencidas'], pg: 'cart-cxc', t: 'Cuentas por cobrar', how: '' },
  { k: ['reporte de pago', 'pago del dia'], pg: 'cart-pagos', t: 'Reporte de pago', how: '' },
  { k: ['historial de pagos'], pg: 'cart-historial', t: 'Historial de pagos',
    how: 'Lleva marcados los documentos duplicados de la fuente: se conservan a propósito y se señalan, no se borran.' },
  { k: ['dias calle', 'cobranza en dias'], pg: 'cart-diascalle', t: 'Cobranza en días calle', how: '' },
  { k: ['facturas cerradas'], pg: 'cart-cerradas', t: 'Facturas cerradas del mes', how: '' },
  { k: ['inactivar cliente', 'inactivacion de clientes', 'solicitud de inactivacion', 'coi'], pg: 'inactcli', t: 'Inactivación Clientes (COI)',
    how: 'Formato COI. <b>Aún no está migrado</b>: es un formulario sin histórico y va con Negociaciones.' },
  { k: ['devolucion', 'devoluciones'], pg: 'devol', t: 'Devoluciones', how: 'Registro y seguimiento de devoluciones. <b>Aún no está migrado.</b>' },
  { k: ['viatico', 'viaticos', 'rendicion', 'gastos'], pg: 'viaticos', t: 'Viáticos y Gastos', how: 'El circuito semanal: el coordinador solicita los jueves (VNT-FOR-019), Compras gestiona hoteles y traslados, Cuentas por Pagar arma la instrucción de pago, la Gerencia de Administración la aprueba, Tesorería paga y cada asesor rinde su gasto con foto. Cada quien ve su bandeja.' },
  { k: ['bitacora', 'farmatodo', 'ftd', 'actividades con farmatodo'], pg: 'bitftd', t: 'Bitácora FTD',
    how: 'Trazabilidad de actividades con Farmatodo: 1.403 registros con filtros por marca, actividad y sucursal, modal de detalle y descarga a CSV. El alta manual aún no está migrada.' },
  { k: ['presentacion', 'presentaciones', 'informe de trade', 'trade'], pg: 'trade', t: 'Trade (informes y presentaciones)',
    how: 'Informes del semestre y descarga de presentaciones en PDF por tipo y mes.' },
  { k: ['power query', 'pquery', 'consulta general', 'dashboard ddh', 'tablero ddh', 'power query ddh'], pg: 'pquery', t: 'Power Query DDH',
    how: 'El <b>Dashboard DDH</b> de C.E.I. (Distribuidora DH): resumen ejecutivo, indicadores, regiones, vendedores, clientes, grupos, canales, marcas, sublíneas, SKU, detalle y exportaciones a Excel. La base la publican Analista C.E.I., Gerencia C.E.I. y Master desde «Actualizar Base», dentro del propio tablero. El de DH Vital es <b>Power Query DHV</b> y el de Difranca, <b>Power Query DIF</b>.' },
  { k: ['dashboard dhv', 'tablero dhv', 'power query dhv', 'pquery dhv'], pg: 'pquery-dhv', t: 'Power Query DHV',
    how: 'El <b>Dashboard DHV</b> de C.E.I. (DH Vital): el mismo tablero que Power Query DDH, con su propia base. La publican Analista C.E.I., Gerencia C.E.I. y Master desde «Actualizar Base», dentro del propio tablero.' },
  { k: ['dashboard dif', 'tablero dif', 'power query dif', 'pquery dif', 'power query difranca', 'dashboard difranca'], pg: 'pquery-dif', t: 'Power Query DIF',
    how: 'El <b>Dashboard DIF</b> de C.E.I. (Difranca · GA.MA Italy): el mismo tablero que Power Query DDH, con su propia base y el volumen en unidades. La publican Analista C.E.I., Gerencia C.E.I. y Master desde «Actualizar Base», dentro del propio tablero.' },
  { k: ['analitica', 'comparador', 'pareto', 'rfm', 'puente interanual', 'estacionalidad', 'what if', 'simulador'], pg: 'an-comparador', t: 'Analítica',
    how: 'Cinco páginas: Comparador, Pareto ABC & RFM, Puente interanual, Estacionalidad y Simulador What-If. Los <b>Insights automáticos</b> eran la sexta y desde el 2026-08-21 viven en Resumen General.' },
  /* Entrada propia desde la mudanza: preguntar por «insights» tiene que llevar a
   * Resumen General, no a Analítica. `pathTo` resuelve el módulo con el permiso
   * de QUIEN pregunta, así que a un asesor le dice su propia ruta. */
  { k: ['insights', 'insight', 'hallazgos', 'anomalias', 'alertas automaticas'], pg: 'an-insights', t: 'Insights automáticos',
    how: 'Los hallazgos del período —anomalías, récords, top movers, caída sostenida, silencio, concentración y cobertura— dentro de tu alcance. Las tarjetas de marca van con las cifras del grupo y lo dicen con el rótulo «grupo».' },
  { k: ['financiero', 'usd', 'dolares', 'dolar', 'margen', 'margenes', 'precio y volumen', 'explorador', 'importes'], pg: 'financiero', t: 'Análisis Financiero (USD)',
    how: 'Siete vistas en una sola página: Resumen ejecutivo, Tendencia, Por empresa, Por marca, Categorías y SKUs, Clientes y Precio y volumen. Es la <b>única</b> sección en USD. Y usa DOS calendarios a la vez: la tendencia va por mes calendario y el resto por año fiscal.' },
  { k: ['resumen general', 'resumen', 'por empresa', 'ddh', 'dhv', 'difranca', 'panorama'], pg: 'resumen', t: 'Resumen General',
    how: 'KPIs con deltas contra el mes y el año anterior, y el selector de empresa dentro de la página (Grupo completo, DDH, DHV, Difranca · GA.MA). Al pie, la tira de «Insights del período».' },
  { k: ['constructor', 'self service', 'autoservicio', 'consulta a la medida', 'armar consulta'], pg: 'ctor', t: 'Constructor',
    how: 'Consultas a la medida. <b>Aún no está migrado</b>: es el módulo que queda entero.' },
  { k: ['informacion de interes', 'documentos', 'manuales', 'politicas'], pg: 'info', t: 'Información de Interés',
    how: 'Documentos internos del grupo. <b>Aún no está migrado.</b>' },
];

/** Palabras que delatan intención de RUTA (`rutaBest` del v88). */
const RE_RUTA = /\b(donde|ruta|seccion|modulo|encuentro|encuentran|consigo|consigue|ubico|ubica|ubicacion|accedo|acceso|llego|abro|queda)\b/;
const RE_RUTA_COMO = /\b(veo|ver|reviso|revisar|consulto|consultar|busco|buscar|registro|registrar|cargo|cargar|hago)\b/;

function rutaBest(qn: string): DefRutaBot | null {
  let best: DefRutaBot | null = null;
  let bs = 0;
  for (const r of RUTAS_BOT) {
    let sc = 0;
    for (const k of r.k) if (qn.includes(k)) sc += k.length;
    if (sc > bs) { bs = sc; best = r; }
  }
  if (!best) return null;
  const intencion = RE_RUTA.test(qn) || (/\bcomo\b/.test(qn) && RE_RUTA_COMO.test(qn));
  if (intencion && bs >= 3) return best;
  return bs >= 8 ? best : null;
}

/* ───────────────────────── FAQ de uso del portal ────────────────────────── */

interface DefFaq { k: string[]; a: string }

/**
 * Las preguntas son las del v88; las respuestas dicen lo que hace ESTE portal.
 * `__CORTE__` se resuelve en caliente con la fecha real de la base.
 */
const FAQ: DefFaq[] = [
  { k: ['export', 'descarg', 'excel', 'xlsx', 'csv'],
    a: 'Hoy exportan a CSV <b>Cartera</b> (cada reporte, con sus filtros y su orden), <b>Bitácora FTD</b> («Descargar detalle», 19 columnas), con punto y coma y BOM para que Excel en español los abra directo; y <b>Power Query</b> exporta a Excel desde la vista «Exportaciones» de su tablero. En el resto de las secciones la descarga <b>todavía no está migrada</b>.' },
  { k: ['tema', 'oscur', 'claro', 'dark', 'modo noche', 'cambiar modo'],
    a: 'El portal tiene tema claro y oscuro, pero el <b>interruptor aún no está migrado</b>: por ahora se sirve el tema claro. No lo cambies a mano en el navegador; queda apuntado como pendiente de la migración.' },
  { k: ['filtr', 'drawer'],
    a: 'Los filtros están <b>dentro de cada página</b>, en su encabezado (período, empresa, región, vendedor, marca… según la sección). No hay un cajón único como en el portal viejo. Y lo que ves ya viene recortado por tu alcance: una fila fuera de tu zona <b>no sale de la base</b>, no es que se oculte.' },
  { k: ['buscar', 'busqueda', 'atajo'],
    a: 'La búsqueda global con atajo de teclado <b>todavía no está migrada</b>. Para encontrar una sección, pregúntame: «¿dónde veo la cartera?». Para buscar un cliente, entra a Clientes → Comportamiento general y usa su buscador.' },
  { k: ['yoy', 'interanual', 'ano anterior', 'contra el ano pasado'],
    a: 'La comparación contra el año anterior es una casilla <b>dentro de cada página</b> que la admite (Resumen, Clientes, Tendencia, Vendedores, detalle de marca). En Analítica hay además una página entera para eso: el <b>Puente interanual</b>.' },
  { k: ['modulo', 'menu', 'seccion', 'navega', 'proceso', 'organizado'],
    a: 'El menú de arriba está organizado <b>por procesos</b>: Resumen General, Almacén y Despacho, C.E.I., Cobranzas, Comercial, Facturación, Finanzas, Gerencia, Inteligencia de Mercado, Trade Marketing, Constructor e Información de Interés. Al pulsar un módulo, su submenú aparece a la izquierda. Solo verás los módulos que tu rol permite.' },
  { k: ['login', 'entrar', 'sesion', 'clave', 'contrasen', 'password', 'iniciar'],
    a: 'Con tu usuario y contraseña. En este portal las contraseñas están <b>cifradas</b> (no como en el portal viejo, que las llevaba en texto dentro del archivo) y la sesión dura 8 horas. Si no recuerdas tus credenciales, escribe a Control Estadístico. Por seguridad no comparto contraseñas.' },
  { k: ['unidad', 'usd', 'dolar', 'moneda', 'importe', 'cifras', 'cajas'],
    a: 'Todo el portal va en <b>cajas netas</b>, salvo dos excepciones que conviene tener presentes: <b>Análisis Financiero</b> va en USD, y <b>Difranca no maneja cajas</b> —sus cifras son unidades, aunque la columna se llame igual—. Y los importes de <b>Cartera</b> son brutos, con el IVA del 16 %.' },
  { k: ['corte', 'actualiz', 'hasta cuando', 'datos hasta', 'fecha de actualizacion'],
    a: '__CORTE__' },
  { k: ['constructor', 'self service', 'autoservicio'],
    a: 'Es el módulo self-service para armar consultas a la medida. <b>Es el único módulo que queda entero por migrar</b>, así que hoy su página está marcada como pendiente.' },
  { k: ['negociacion', 'mer037', 'aporte'],
    a: 'En <b>Comercial → Negociaciones Comerciales</b>. Es una página de <b>escritura</b>: la solicitud queda guardada en la base y avisa por correo a los buzones que correspondan. El techo es el <b>presupuesto del trimestre</b> de cada asesor — la vieja regla del 3 % ya no dimensiona la solicitud, sino la bolsa.' },
  { k: ['cartera', 'cobranza', 'por cobrar', 'vencid'],
    a: 'En <b>Cobranzas → Cartera de Clientes</b>, con seis reportes en el submenú. Dos cosas que hay que saber antes de usar sus cifras: los importes llevan el <b>IVA del 16 %</b>, y el historial trae documentos <b>duplicados de la fuente</b> que se conservan marcados a propósito.' },
  { k: ['inactiva', 'coi'],
    a: 'En <b>Cobranzas → Inactivación Clientes</b> (formato COI). Es un formulario y <b>todavía no está migrado</b>; su página está marcada como pendiente.' },
  { k: ['viatico', 'gasto', 'rendicion'],
    a: 'En <b>Finanzas → Viáticos y Gastos</b>. <b>Todavía no está migrado.</b>' },
  { k: ['devolucion', 'almacen', 'despacho'],
    a: 'En <b>Almacén y Despacho → Devoluciones</b>. <b>Todavía no está migrado.</b>' },
  { k: ['lanzamiento', 'kits'],
    a: 'Seguimiento de los lanzamientos: 5 kits más HD Cosmetic\'s, con unidades netas, detalle región × serie × mes, clientes de la línea, forecast a 3 meses y el bloque de recompras de Argán. Cuidado con una trampa heredada: el selector de meses <b>mueve el gráfico y no las tarjetas</b>, y los tres bloques tienen fechas de corte distintas.' },
  { k: ['meta', 'cumplimiento', 'objetivo'],
    a: 'En el submódulo <b>Metas</b> (C.E.I., Comercial, Finanzas o Gerencia, según tu rol). La meta va en cajas para DDH/DHV y en unidades para Difranca, y los dos grupos <b>nunca se suman</b>: la página muestra uno o el otro.' },
  { k: ['360', 'panel sku', 'detalle sku'],
    a: 'El panel 360° del SKU del portal viejo <b>todavía no está migrado</b>. Mientras tanto: Análisis Financiero → «Categorías y SKUs» da el SKU en USD, y Analítica → Pareto lo ordena por volumen.' },
  { k: ['grafic', 'interactiv', 'drill'],
    a: 'Los gráficos respetan los filtros de su página. El drill-down por clic del portal viejo <b>todavía no está migrado</b> en todas las vistas.' },
  /* `__IA__` se resuelve en caliente, como `__CORTE__`: el texto depende de si
   * el despliegue tiene la IA configurada, y eso solo se sabe en runtime. */
  { k: ['asistente', 'chatbot', 'inteligencia artificial', 'online', 'offline', 'modo local', 'dhalia', 'ia'],
    a: '__IA__' },
  { k: ['permiso', 'rol', 'acceso', 'no veo', 'bloquead'],
    a: 'El acceso es por rol, y aquí se aplica <b>en la base de datos</b>: los regionales ven sus vendedores, los asesores su cartera, y Trade Marketing, Cobranzas y Facturación sus módulos. Además hay una página con candado propio: la <b>Bitácora FTD</b> es de Key Accounts cuando se entra por Comercial, y está completa desde Trade Marketing. Si necesitas otro acceso, escribe a Control Estadístico.' },
  { k: ['soporte', 'error', 'falla', 'contact', 'reportar'],
    a: 'Escribe a <b>Control Estadístico Interno</b> (analistacontrolestadistico@grupo-dh.com) con tu usuario, la sección y una captura.' },
];

function faqBest(qn: string): DefFaq | null {
  let best: DefFaq | null = null;
  let bs = 0;
  for (const f of FAQ) {
    let s = 0;
    for (const k of f.k) if (qn.includes(k)) s += k.length;
    if (s > bs) { bs = s; best = f; }
  }
  return bs >= 4 ? best : null;
}

/* ──────────────── dimensión y entidades fijas del dominio ──────────────── */

const DIMK: Record<string, string> = {
  cliente: 'Cliente', grupo: 'Grupo', marca: 'Marca', vend: 'Vendedor',
  region: 'Región', estado: 'Estado', empresa: 'Empresa',
  categoria: 'Categoría', sublinea: 'Sublínea', articulo: 'Artículo',
};

/** `iRuta` del v88: qué dimensión pregunta la frase. Solo regex, sin datos. */
const DIMS_RE: [string, RegExp][] = [
  ['articulo', /\bskus?\b|\barticulos?\b|\bproductos?\b/],
  ['cliente', /\bclientes?\b|\bsucursal/],
  ['grupo', /\bgrupos?\b|\bcadena/],
  ['marca', /\bmarcas?\b/],
  ['vend', /\bvendedor/],
  ['region', /\bregion|\bzona/],
  ['estado', /\bestados?\b/],
  ['empresa', /\bempresas?\b/],
  ['categoria', /\bcategorias?\b/],
  ['sublinea', /\bsublineas?\b/],
];

/**
 * Las únicas entidades que se reconocen sin ir a la base: **6 de las 7 marcas**
 * de `core.marca` y las 3 empresas. Son hechos fijos del dominio, documentados
 * en `CLAUDE.md` §1. Los clientes, vendedores y SKUs viven en la base y no se
 * traen al navegador — ver la nota de cabecera.
 *
 * **`Otros` se omite a propósito**: es el cajón de sastre del catálogo, no una
 * marca por la que nadie pregunte, y aceptarla como entidad haría que «otros»
 * en mitad de una frase se leyera como nombre de marca.
 *
 * Y el catálogo que se usa aquí es el de `core.marca` — «HD Cosmetic's» con
 * **apóstrofo recto** y «GA.MA» **con puntos**. Ojo al portar cualquier cosa
 * de Negociaciones: allí las mismas dos son «HD Cosmetic´s» con **acento agudo** y
 * «GAMA» **sin puntos** (`negociaciones.mts`), y en `gtm` son «HD Cosmetics» y
 * «GA.MA». Son catálogos distintos y no se unifican (gotcha 52).
 */
/**
 * El corte de datos, con **cinco** estados y no tres.
 *
 * Antes era `string | null`, y ahí estaba el fallo: `null` significaba a la vez
 * «todavía no ha contestado» y «no se puede», así que `corteTxt()` respondía
 * «tu usuario no tiene Resumen General» **mientras la petición estaba en
 * vuelo**. Con el arranque en frío de Neon (~1 s) contra los 120 ms que espera
 * `enviar`, eso le pasaba a un MASTER pulsando el chip que sale de serie.
 * `'denegado'` y `'error'` van separados porque no son lo mismo: uno es el
 * perfil y el otro es la red.
 *
 * Desde que el corte va por `/api/dhalia/corte` —que **no exige página**—,
 * `'denegado'` solo puede salir de un 401: la sesión caducada. Ya no lo puede
 * disparar un rol, que era justo el fallo de `ALMACENYDESPACHO`. El estado se
 * mantiene porque la sesión sí caduca (TTL de 8 h) y confundir eso con un fallo
 * de red mandaría a la usuaria a reintentar en vez de a volver a entrar.
 */
type EstadoCorte =
  | { t: 'inicial' }
  | { t: 'cargando' }
  | { t: 'denegado' }
  | { t: 'error' }
  | { t: 'ok'; min: string; max: string };

/** `MES_N` del v88, verbatim y en su orden. */
const MES_N = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * `perLbl()` del v88: `MES_N[m-1] + ' ' + año` → «Jul 2026». El endpoint
 * devuelve la fecha ISO del día (`2026-07-28`) y la asistente del v88 habla en
 * meses, porque su cubo es mensual. Se corta la cadena en vez de construir un
 * `Date`: `new Date('2026-07-28')` es medianoche **UTC** y en Venezuela (UTC−4)
 * retrocede al día 27, que en un cambio de mes retrocedería el mes entero.
 */
function perLbl(iso: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${MES_N[+m[2]! - 1] ?? m[2]} ${m[1]}`;
}

const ENTIDADES: { dim: string; lbl: string; k: string[] }[] = [
  { dim: 'marca', lbl: 'BBK', k: ['bbk'] },
  { dim: 'marca', lbl: "HD Cosmetic's", k: ['hd cosmetic', 'hd cosmetics', 'hd'] },
  { dim: 'marca', lbl: 'Pasarela', k: ['pasarela'] },
  { dim: 'marca', lbl: 'Quick Fix', k: ['quick fix', 'quickfix', 'quick'] },
  { dim: 'marca', lbl: 'GA.MA', k: ['ga ma', 'gama'] },
  { dim: 'marca', lbl: 'FTD', k: ['ftd', 'farmatodo'] },
  { dim: 'empresa', lbl: 'DDH · Díaz Hernández', k: ['ddh', 'diaz hernandez'] },
  { dim: 'empresa', lbl: 'DHV · DH Vital', k: ['dhv', 'dh vital'] },
  { dim: 'empresa', lbl: 'Difranca', k: ['difranca', 'dif'] },
];

function entidadEn(qn: string): { dim: string; lbl: string } | null {
  const toks = qn.split(' ');
  let best: { dim: string; lbl: string; sc: number } | null = null;
  for (const e of ENTIDADES) {
    for (const k of e.k) {
      const pega = k.includes(' ') ? qn.includes(k) : toks.includes(k);
      if (pega && (!best || k.length > best.sc)) best = { dim: e.dim, lbl: e.lbl, sc: k.length };
    }
  }
  return best ? { dim: best.dim, lbl: best.lbl } : null;
}

/** `dataWords` del v88: la frase pide cifras. */
const RE_DATOS = /\b(venta|ventas|vendio|vendido|vendimos|cajas|facturo|facturacion|cuanto|cuanta|cuantos|cuantas|total|totales|acumulado|cifra|cifras|monto|montos|importe|top|mejores|principales|ranking|mayores|participacion|mix|share|evolucion|tendencia|compara|comparar|vs|versus)\b/;

const NOCIFRAS = 'Yo no comparto cifras 😉 — te indico dónde consultarlas con tus filtros:';

const CAPS = 'Puedo ayudarte con:<br>'
  + '• <b>Rutas del portal</b>: dime qué buscas y te digo dónde está <i>según tu perfil</i> — '
  + '«¿dónde veo los lanzamientos?», «¿dónde está la cartera?», «¿dónde consigo las ventas de BBK?».<br>'
  + '• <b>Uso del portal</b>: filtros, exportar, unidades, corte de datos, permisos.<br>'
  + 'No comparto cifras: te llevo a la sección donde consultarlas con tu alcance.';

/* ───────────────────────── capa de IA en línea (Claude) ─────────────────── */

/**
 * La D-1 de `04_DOCS/ESTADO_DHALIA.md`, respondida el 2026-08-14: la IA se
 * activa, y con la arquitectura de §8.5 — NO con la del v88. Aquí ya no hay
 * `DHALIA_URL` ni `DHALIA_KEY` en `localStorage`, ni un `config key` que
 * teclear: el navegador habla SOLO con `/api/dhalia/chat` (mismo origen, así
 * que el CSP `connect-src 'self'` sigue intacto) y es esa función la que llama
 * a Claude con la clave en variables de entorno de Netlify. Si el despliegue
 * no tiene clave, el sondeo dice «no disponible» y la asistente se queda en
 * modo local, exactamente como antes.
 *
 * QUIÉN CONTESTA (cambiado el 2026-08-19, a pedido de la usuaria). Hasta esa
 * fecha el motor local contestaba PRIMERO y a la IA solo le llegaba lo que el
 * guion no supo; el efecto era que un «hola» recibía siempre la misma frase
 * enlatada y la asistente sonaba a formulario. Ahora, con la IA activa,
 * contesta ella todo — y el motor local le pasa su respuesta como `pista`, que
 * es donde viven los nombres de sección y los clics exactos. Sin clave, o si la
 * IA falla, el motor local vuelve a contestar directo: nadie se queda sin
 * respuesta. El coste de eso es una llamada por mensaje en vez de una por
 * pregunta rara; con `claude-haiku-4-5` son céntimos, y el techo de créditos de
 * la cuenta lo acota (Netlify → Team settings → AI).
 */

/* ──────────────────────────── el componente ─────────────────────────────── */

interface Mensaje { id: number; quien: 'bot' | 'usr'; html: string }

const CHIPS = [
  '¿Qué puedes hacer?',
  '¿Dónde veo los lanzamientos?',
  '¿Dónde está la cartera?',
  '¿Hasta cuándo hay datos?',
  '¿Cómo exporto los datos?',
];

export default function Dhalia() {
  const { sesion } = useSesion();
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const [corte, setCorte] = useState<EstadoCorte>({ t: 'inicial' });
  const [estado, setEstado] = useState('Modo local');
  const [enLinea, setEnLinea] = useState(false);
  const finRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(0);
  /**
   * `HIST` del v88: las últimas 12 vueltas, solo para el modo en línea. **Al
   * relé se le mandan las 6 ANTERIORES a la pregunta en curso, no las 12** —
   * el v88 hacía `hist: HIST.slice(-6)` y este porte llegó a mandar el doble
   * (H5 de `ESTADO_DHALIA.md`). Importa porque el historial lleva las
   * preguntas literales del usuario, que es justo donde aparecen los nombres
   * de cliente. El relé vuelve a recortar en el servidor (6 vueltas, 500
   * caracteres cada una): este límite del cliente es cortesía, aquel es ley.
   */
  const histRef = useRef<{ role: string; content: string }[]>([]);

  const permisos = useMemo(() => permisosDeSesion(sesion), [sesion]);

  /**
   * El contexto que viaja con cada pregunta a la IA: las secciones que ESTE
   * usuario puede ver, con su ruta real en el menú. Es estructura de
   * navegación —lo mismo que pinta el menú—, no datos: ni cifras, ni clientes,
   * ni nada de la base. Con esto la IA responde «¿dónde veo X?» con la ruta
   * verdadera del perfil, en vez de inventarse secciones.
   */
  const seccionesIA = useMemo(() => {
    const vistas = new Set<string>();
    const out: string[] = [];
    for (const r of RUTAS_BOT) {
      if (vistas.has(r.pg)) continue;
      vistas.add(r.pg);
      const ubs = ubicacionesDe(r.pg, permisos);
      if (ubs === 'denegada' || !ubs.length) continue;
      const u = ubs[0]!;
      out.push(`${r.t} — ${u.modulo}${u.submodulo ? ` → ${u.submodulo}` : ''}${u.hoja ? ` → ${u.hoja}` : ''}`);
    }
    return out;
  }, [permisos]);

  const nuevo = useCallback((quien: 'bot' | 'usr', html: string) => {
    idRef.current += 1;
    setMensajes((m) => [...m, { id: idRef.current, quien, html }]);
  }, []);

  /* Al cerrar sesión se limpia todo, como el `setInterval` del v88 que borra
   * los mensajes cuando `SESSION.user` desaparece. */
  useEffect(() => {
    if (!sesion) {
      setAbierto(false); setMensajes([]); setCorte({ t: 'inicial' });
      histRef.current = [];
    }
  }, [sesion]);

  /**
   * El `health()` del v88, contra el relé propio. Una sola petición al abrir:
   * si el despliegue tiene `ANTHROPIC_API_KEY`, el encabezado dice «IA en
   * línea»; si no, «Modo local» — y `enviar` ni intenta llamar a la IA.
   */
  useEffect(() => {
    if (!abierto || !sesion) return;
    let vivo = true;
    dhaliaApi.ia()
      .then((d) => {
        if (!vivo) return;
        setEnLinea(d.disponible);
        setEstado(d.disponible ? 'IA en línea · Claude' : 'Modo local');
      })
      .catch(() => { if (vivo) { setEnLinea(false); setEstado('Modo local'); } });
    return () => { vivo = false; };
  }, [abierto, sesion]);

  useEffect(() => { finRef.current?.scrollIntoView({ block: 'end' }); }, [mensajes, pensando]);

  /**
   * El corte de datos. Se pide una sola vez, al abrir.
   *
   * ✅ **Ya NO sale de `/api/resumen/diario`**, y ese cambio es el arreglo de
   * raíz del H2/H3 de `04_DOCS/ESTADO_DHALIA.md` (§9, punto 6). Antes esta
   * pregunta —uno de los cinco chips de serie— exigía la página `resumen`, y
   * `ALMACENYDESPACHO` es el único rol que no la tiene: a ese usuario la
   * asistente le contestaba «no puedo leer el corte con tu perfil» a una
   * pregunta cuya respuesta no es ningún secreto. Ahora va por
   * `/api/dhalia/corte`, que **solo pide sesión**. Con eso desaparece el
   * `permisos.puedeVer('resumen')` que había aquí, y el estado `denegado` queda
   * para lo único que puede denegarlo de verdad: no tener sesión.
   *
   * El endpoint devuelve **los dos rangos** —el global del cubo y el del
   * alcance del usuario, intersecado en SQL— porque la decisión **D-2** (cuál
   * se enseña) sigue abierta. Lo que se pinta hoy es el **global**, que es lo
   * que hace el v88: su `corteTxt()` da el rango a secas y pone el ámbito
   * aparte, en `scopeNote()`. Cambiar a `alcance` cuando la usuaria decida es
   * una línea, y el dato ya viaja en la respuesta.
   */
  useEffect(() => {
    if (!abierto || corte.t !== 'inicial' || !sesion) return;
    setCorte({ t: 'cargando' });
    dhaliaApi.corte()
      .then((d) => setCorte({ t: 'ok', min: d.global.min, max: d.global.max }))
      .catch((e) => setCorte({
        t: e instanceof ApiError && (e.status === 401 || e.status === 403) ? 'denegado' : 'error',
      }));
  }, [abierto, corte.t, sesion]);

  /**
   * `scopeNote()` del v88 (`pmbot.js`), que este porte no tenía. Va pegado a la
   * respuesta del corte, como allí (`cb(corteTxt()+scopeNote())`).
   *
   * **Es la pieza que faltaba para que lo del alcance se diga sin mentir.** El
   * v88 nunca metió el alcance dentro de la frase del rango: da el rango a
   * secas —que es global— y pone el ámbito **aparte**, referido al usuario. El
   * porte había fundido las dos cosas en «Rango disponible con tu alcance», y
   * de ahí salía la afirmación falsa. `'tu zona'` es el respaldo literal del
   * v88 cuando la sesión no trae etiqueta.
   */
  const notaAmbito = useCallback((): string => {
    const acotado = !!(sesion?.alcance.vendedores || sesion?.alcance.regiones
      || sesion?.alcance.clientes || sesion?.alcance.grupos);
    return `<span class="dh-src">Ámbito: ${acotado ? 'tu zona (solo tus vendedores)'
      : 'todas las empresas'} · cajas netas</span>`;
  }, [sesion]);

  /** La FAQ del propio asistente. Dinámica porque depende de si la IA está
   * activa en ESTE despliegue, y eso solo lo sabe el sondeo. */
  const iaTxt = useCallback((): string => (
    'Soy <b>DHALIA</b> (las siglas DH de GrupoDH + dalia). Tengo dos modos. En <b>modo local</b> '
    + 'te digo por dónde se llega a cada sección <i>según tu perfil</i> y te resuelvo dudas de uso. '
    + (enLinea
      ? 'Ahora mismo te contesto con <b>IA (Claude)</b> a través del servidor del portal, '
        + 'apoyándome en ese buscador de rutas para no equivocarme con los nombres de las '
        + 'secciones — sin cifras ni datos de clientes: solo tu pregunta y qué secciones '
        + 'puedes ver. Si la IA se cae, sigo respondiendo en modo local. '
      : 'El modo de <b>IA en línea</b> no está activo en este despliegue, así que nada de lo que '
        + 'escribes aquí sale del portal. ')
    + 'En ningún modo comparto <b>cifras</b>, y es a propósito: se consultan en su sección, '
    + 'con tus filtros y tu alcance.'
  ), [enLinea]);

  const corteTxt = useCallback((): string => {
    switch (corte.t) {
      case 'inicial':
      case 'cargando':
        return 'Un momento, que lo estoy consultando…';
      case 'denegado':
        /* Desde que el corte va por `/api/dhalia/corte` —que solo pide sesión—
         * esto ya no es «tu perfil no llega»: es que la sesión caducó. El texto
         * de antes nombraba el Resumen General, y con el endpoint nuevo eso ya
         * no tiene nada que ver. */
        return 'Tu sesión ha caducado, así que no puedo consultarlo. '
          + 'Vuelve a entrar y pregúntamelo otra vez.';
      case 'error':
        return 'No he podido leer el corte ahora mismo. Vuelve a preguntármelo en un momento.';
      default:
        /* Verbatim del v88, incluido «mensuales» y el guion largo con espacios.
         * El rango va sin adjetivo: es el del cubo, y quien lo acota es la nota
         * de ámbito que se pega debajo. */
        return `Los datos mensuales llegan hasta <b>${esc(perLbl(corte.max))}</b> (el mes en curso `
          + `puede estar parcial). Rango disponible: ${esc(perLbl(corte.min))} – `
          + `${esc(perLbl(corte.max))}.`
          + notaAmbito();
    }
  }, [corte, notaAmbito]);

  /**
   * `rutaHTML` del v88: la ubicación de una página PARA ESTE USUARIO, con sus
   * alternativas. Si el perfil no la tiene, lo dice — no la esconde en silencio.
   */
  const rutaHTML = useCallback((pg: string, titulo: string, how: string): string => {
    const ubs = ubicacionesDe(pg, permisos);
    if (ubs === 'denegada' || !ubs.length) {
      return `📍 <b>${esc(titulo)}</b><br>Tu perfil no tiene acceso a esta sección. `
        + 'Si la necesitas, escribe a Control Estadístico.';
    }
    const u = ubs[0]!;
    const camino = `<b>${esc(u.modulo)}</b>`
      + (u.submodulo ? ` → <b>${esc(u.submodulo)}</b>` : '')
      + (u.hoja ? ` → <b>${esc(u.hoja)}</b>` : '');
    let html = `📍 <b>${esc(titulo)}</b><br>Ruta: ${camino} (menú superior).`;
    html += `<br><a class="dh-ir" href="${esc(u.ruta)}?mod=${esc(u.modId)}">Llévame ahí →</a>`;
    if (how) html += `<br>${how}`;
    const alt = [...new Set(ubs.slice(1).map((x) => x.modulo))].filter((x) => x !== u.modulo);
    if (alt.length) {
      html += `<br><span class="dh-src">También disponible en: ${esc(alt.slice(0, 3).join(', '))}</span>`;
    }
    return html;
  }, [permisos]);

  /** `iRuta` del v88: pregunta de cifras → la sección donde se consultan. */
  const iRuta = useCallback((qn: string): string => {
    const ent = entidadEn(qn);
    let dim: string | null = null;
    for (const [d, re] of DIMS_RE) if (!dim && re.test(qn)) dim = d;
    if (!dim && ent) dim = ent.dim;

    const quien = ent ? `Para <b>${esc(ent.lbl)}</b> (${DIMK[ent.dim] ?? ent.dim}):<br>` : '';
    let tgt: [string, string, string];
    if (dim === 'marca') {
      tgt = ['marcas', 'Marcas', 'Elige la marca y el rango. Para las mismas cifras en USD: Análisis Financiero → «Por marca».'];
    } else if (dim === 'articulo') {
      tgt = ['financiero', 'Análisis Financiero → «Categorías y SKUs»', 'Es donde hoy se ve el SKU (en USD). El panel 360° del portal viejo aún no está migrado.'];
    } else if (dim === 'cliente' || dim === 'grupo') {
      tgt = ['clientes', 'Clientes', 'En <b>Comportamiento general</b> se busca por nombre o código; Tendencia y Análisis en el mismo submódulo.'];
    } else if (dim === 'vend') {
      tgt = ['vendedor', 'Vendedores', 'Panel general por vendedor; Evolución, Análisis y Comportamiento en el mismo submódulo.'];
    } else if (dim === 'region' || dim === 'estado') {
      tgt = ['resumen', 'Resumen General', 'Trae el desglose por región; también lo hay en Vendedores y en Analítica.'];
    } else if (dim === 'empresa') {
      tgt = ['resumen', 'Resumen General', 'El selector de empresa está en la propia página (Grupo completo, DDH, DHV, Difranca · GA.MA).'];
    } else if (dim === 'categoria' || dim === 'sublinea') {
      tgt = ['financiero', 'Análisis Financiero (USD)', 'Vista «Categorías y SKUs».'];
    } else {
      tgt = ['resumen', 'Resumen General', 'Es el panorama del grupo; desde ahí bajas a la sección que necesites.'];
    }
    return `${NOCIFRAS}<br>${quien}${rutaHTML(tgt[0], tgt[1], tgt[2])}`;
  }, [rutaHTML]);

  /**
   * `answer` del v88, con el mismo orden de decisión. Devuelve `null` cuando el
   * motor local no sabe: es el único punto en que el v88 consulta a la IA.
   */
  const responder = useCallback((q: string): string | null => {
    const qn = nrm(q);
    if (!qn) return 'Escribe una pregunta.';

    /* `config url|key …` era el mecanismo del v88 y SE RETIRÓ a propósito
     * (`ESTADO_DHALIA.md` §8.6): la clave ya no existe en ningún navegador. Se
     * contesta al comando —MASTER lo recuerda— en vez de dejarlo caer al
     * «no encontré». */
    if (/^config\s+(url|key)\b/i.test(q.trim())) {
      return 'Ese comando se retiró: mi IA ya no se configura desde el navegador. '
        + 'La clave vive en las <b>variables de entorno de Netlify</b> '
        + '(<code>ANTHROPIC_API_KEY</code>) y el estado del encabezado dice si está activa.';
    }

    if (/^(hola|buenos dias|buenas tardes|buenas noches|buenas|hey|saludos)\b/.test(qn)) {
      return `¡Hola${sesion ? `, ${esc(sesion.usuario.nombre)}` : ''}! 👋 ${CAPS}`;
    }
    if (/\b(gracias|muchas gracias|excelente|perfecto)\b/.test(qn) && qn.length < 30) {
      return '¡A la orden! ¿Algo más en lo que pueda ayudarte?';
    }
    if (/que puedes hacer|capacidades|ayuda\b|como funcionas|que sabes hacer|para que sirves/.test(qn)) {
      return CAPS;
    }
    if (/como te llamas|cual es tu nombre|quien eres|tu nombre/.test(qn)) {
      return 'Me llamo <b><span class="dh-dh">DH</span>ALIA</b>: llevo las siglas de Grupo<b>DH</b> '
        + 'y florezco como una dalia 🌸. Soy la asistente virtual del Portal Master.';
    }
    if (/hasta cuando|corte de datos|datos hasta|fecha de actualizacion|actualizado/.test(qn)) {
      return corteTxt();
    }

    const rt = rutaBest(qn);
    if (rt) return rutaHTML(rt.pg, rt.t, rt.how);
    if (RE_DATOS.test(qn)) return iRuta(qn);
    const ent = entidadEn(qn);
    if (ent && qn.length < 45) return iRuta(qn);

    const f = faqBest(qn);
    if (f) {
      if (f.a === '__CORTE__') return corteTxt();
      if (f.a === '__IA__') return iaTxt();
      return f.a;
    }

    return null;
  }, [sesion, corteTxt, iaTxt, rutaHTML, iRuta]);

  const NO_SE = 'No encontré una respuesta directa (estoy en <b>modo local</b>). Prueba con:<br>'
    + '• «¿dónde veo …?» — lanzamientos, cartera, metas, un cliente, una marca, el financiero<br>'
    + '• o pregúntame por el uso del portal: filtros, exportar, unidades, corte de datos, permisos.';

  const enviar = useCallback((q?: string) => {
    const pregunta = (q ?? texto).trim();
    if (!pregunta || pensando) return;
    if (q === undefined) setTexto('');
    nuevo('usr', esc(pregunta));
    setPensando(true);
    /* Las vueltas ANTERIORES a esta pregunta: es lo que viaja como historial.
     * La pregunta va aparte en el cuerpo, así el relé no la cuenta dos veces. */
    const previas = histRef.current.slice(-6)
      .filter((h): h is { role: 'user' | 'assistant'; content: string } =>
        h.role === 'user' || h.role === 'assistant');
    histRef.current = [...histRef.current, { role: 'user', content: pregunta.slice(0, 400) }].slice(-12);

    /* El v88 espera 120 ms antes de responder: da la sensación de que escribe
     * y evita que la respuesta aparezca antes que la pregunta. */
    window.setTimeout(() => {
      const local = responder(pregunta);
      const cerrar = (html: string) => {
        nuevo('bot', html);
        histRef.current = [...histRef.current,
          { role: 'assistant', content: html.replace(/<[^>]*>/g, ' ').slice(0, 500) }].slice(-12);
        setPensando(false);
      };
      /* Con la IA activa responde ELLA, hasta a un «hola». Lo pidió la usuaria
       * el 2026-08-19: «que actúe como actúa la IA y no dé respuestas de un
       * guion». Antes el motor local contestaba PRIMERO y a la IA solo le
       * llegaba lo que el guion no supo, así que un saludo nunca la alcanzaba.
       *
       * El motor local no se retira: cambia de papel. Su respuesta viaja como
       * `pista` —trae los nombres de sección y los clics exactos, que la IA no
       * puede inventarse— y sigue siendo la red de seguridad cuando la IA falla
       * o cuando el despliegue no tiene clave. Todo pasa por el relé, así que la
       * política de «no dar cifras» sigue viviendo en el servidor. */
      if (!enLinea) { cerrar(local ?? NO_SE); return; }
      const pista = local === null
        ? '' : local.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      dhaliaApi.chat({
        pregunta,
        hist: previas,
        secciones: seccionesIA,
        ...(pista ? { pista } : {}),
        ...(corte.t === 'ok' ? { corte: corte.max } : {}),
      }).then(({ texto: txt }) => {
        cerrar(`${esc(txt).replace(/\n/g, '<br>').replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')}`
          + '<span class="dh-src">Respuesta generada con IA (Claude)</span>');
      }).catch((e: unknown) => {
        /* La IA falló o tardó: se degrada al modo local con la verdad. */
        cerrar(e instanceof ApiError && e.status === 504
          ? 'La IA tardó demasiado en responder. Inténtalo otra vez en un momento, '
            + 'o pregúntame con otras palabras — en modo local resuelvo rutas y uso del portal.'
          : (local ?? NO_SE));
      });
    }, 120);
  }, [texto, pensando, nuevo, responder, enLinea, seccionesIA, corte, NO_SE]);

  const abrir = useCallback(() => { setAbierto((a) => !a); }, []);

  /**
   * El saludo, en un efecto y **no dentro del updater de `setAbierto`**, que es
   * donde estaba. Un updater de estado tiene que ser puro, y este llamaba a
   * `nuevo()` —o sea, a otro `setState`— desde dentro. `main.tsx` monta la app
   * en `<StrictMode>`, que en desarrollo **invoca los updaters dos veces**: dos
   * burbujas de saludo. En producción hoy no se reproducía, pero React puede
   * volver a ejecutar un updater cuando le convenga, así que era un fallo
   * latente, no un detalle de desarrollo.
   *
   * Aquí no se duplica: al abrir, el efecto corre una vez y `nuevo()` deja
   * `mensajes.length` en 1, así que la siguiente pasada sale por la guarda.
   */
  useEffect(() => {
    if (!abierto || mensajes.length) return;
    nuevo('bot', `¡Hola${sesion ? `, <b>${esc(sesion.usuario.nombre)}</b>` : ''}! Soy `
      + '<b><span class="dh-dh">DH</span>ALIA</b>, la asistente virtual del Portal GrupoDH.'
      + `<br>${CAPS}`);
  }, [abierto, mensajes.length, nuevo, sesion]);

  /** Los «Llévame ahí →» del bot son enlaces internos: los enruta la SPA. */
  const clicEnMensaje = useCallback((e: ClicReact<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest('a.dh-ir');
    if (!a) return;
    e.preventDefault();
    const href = a.getAttribute('href');
    if (href) { setAbierto(false); navigate(href); }
  }, [navigate]);

  if (!sesion) return null;

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        aria-expanded={abierto}
        aria-label="DHALIA · Asistente Virtual GrupoDH"
        title="DHALIA · Asistente Virtual GrupoDH"
        /* Anclaje del recorrido guiado (db/080). Ver `lib/recorrido.ts`. */
        data-recorrido="dhalia"
        className="fixed bottom-5 right-5 z-40 grid h-14 w-14 place-items-center rounded-full
                   bg-gradient-to-br from-magenta-fondo to-magenta-fondo-2 text-xl font-black text-white
                   shadow-lg transition hover:scale-105 focus-visible:scale-105"
      >
        {abierto ? '✕' : (
          <img
            src={AVATAR_DHALIA}
            alt=""
            aria-hidden="true"
            className="h-11 w-11 rounded-full object-cover"
          />
        )}
      </button>

      {abierto && (
        <div
          role="dialog"
          aria-label="DHALIA · Asistente Virtual"
          className="fixed bottom-[86px] right-5 z-40 flex max-h-[min(620px,calc(100vh-120px))]
                     w-[min(392px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-pieza
                     border border-borde bg-panel shadow-pieza"
        >
          <div className="flex items-center gap-3 bg-gradient-to-r from-magenta-fondo to-magenta-fondo-2 px-4 py-3 text-white">
            <img
              src={HERO_DHALIA}
              alt=""
              aria-hidden="true"
              className="h-9 w-9 shrink-0 rounded-full bg-white/20 object-cover"
            />
            <div className="min-w-0 leading-tight">
              <b className="block text-sm">
                <span className="text-[#FFD166]">DH</span>ALIA · Asistente GrupoDH
              </b>
              <span className="text-[11px] opacity-85">{estado}</span>
            </div>
            <button
              type="button"
              onClick={() => setAbierto(false)}
              className="ml-auto shrink-0 rounded px-2 text-sm hover:bg-white/20"
              title="Cerrar"
            >
              ✕
            </button>
          </div>

          <div
            onClick={clicEnMensaje}
            className="dh-msgs flex-1 space-y-2 overflow-y-auto p-3 text-[13px] leading-relaxed"
          >
            {mensajes.map((m) => (
              <div
                key={m.id}
                className={
                  'max-w-[88%] rounded-int px-3 py-2 '
                  + (m.quien === 'usr'
                    ? 'ml-auto bg-magenta-fondo text-white'
                    : 'bg-suave text-texto')
                }
                // El HTML lo construye este archivo: todo lo que viene de fuera
                // (la pregunta, los nombres) pasa por `esc()` antes de entrar.
                dangerouslySetInnerHTML={{ __html: m.html }}
              />
            ))}
            {pensando && (
              <div className="w-16 rounded-int bg-suave px-3 py-2 text-texto-2">
                <span className="animate-pulse">● ● ●</span>
              </div>
            )}
            <div ref={finRef} />
          </div>

          <div className="flex flex-wrap gap-1 border-t border-borde px-3 py-2">
            {CHIPS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => enviar(c)}
                className="rounded-full border border-borde px-2.5 py-1 text-[11px]
                           text-texto-2 transition hover:bg-suave"
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t border-borde p-2">
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); enviar(); } }}
              maxLength={400}
              placeholder="Escribe tu pregunta…"
              aria-label="Pregunta para DHALIA"
              className="min-w-0 flex-1 rounded-int bg-suave px-3 py-2 text-[13px]
                         outline-none placeholder:text-lavanda"
            />
            <button
              type="button"
              onClick={() => enviar()}
              disabled={pensando}
              title="Enviar"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-magenta-fondo
                         text-white transition hover:opacity-90 disabled:opacity-40"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
